import { yupResolver } from '@hookform/resolvers/yup'
import Decimal from 'decimal.js'
import { useCallback, useMemo } from 'react'
import { useForm } from 'react-hook-form'
import * as yup from 'yup'
import { useTranslation } from 'react-i18next'
import { useRedeemPointCalculation } from '../hook/useRedeemPointCalculation'
import {
    LoyaltyRedeemPointRuleSettingContract,
    CustomerLookupContract,
    OrderLookupContract,
} from 'models'
import { min } from 'lodash-es'

interface IForm {
    pointToRedeem: number
    discount: number
}

function usePaymentRedeemPoint({
    pointRedemptionSetting,
    customer,
    orderDetail,
}: {
    pointRedemptionSetting: LoyaltyRedeemPointRuleSettingContract | undefined
    customer: CustomerLookupContract
    orderDetail?: OrderLookupContract
}) {
    const { t } = useTranslation()

    const customerPointBalance = useMemo<number>(
        () => Math.trunc((customer?.pointBalance ?? 0) * 100) / 100,
        [customer?.pointBalance],
    )

    const {
        newTotal,
        calculateDiscountToPoint,
        calculateRedeemedPointToDiscount,
        calculateNewTotalDueAfterDisocunt,
        maxDiscount,
        floorPoint,
        requiredPointToRedeem,
    } = useRedeemPointCalculation({
        pointRedemptionSetting,
        orderDetail,
    })

    const maxPointCanUsed = useMemo<number>(() => {
        var maxPoint = calculateDiscountToPoint(maxDiscount)
        maxPoint = floorPoint(maxPoint)
        if (maxPoint > customerPointBalance)
            return floorPoint(customerPointBalance)
        else return maxPoint
    }, [
        calculateDiscountToPoint,
        maxDiscount,
        floorPoint,
        customerPointBalance,
    ])

    const form = useForm<IForm>({
        defaultValues: {
            pointToRedeem: maxPointCanUsed,
            discount: maxDiscount.toNumber(),
        },
        resolver: yupResolver(
            yup.object().shape({
                pointToRedeem: yup
                    .number()
                    .min(
                        0,
                        t('finish.warning.require_point', {
                            point: 0,
                            count: 0,
                        }),
                    )
                    .max(
                        customerPointBalance,
                        t('finish.warning.maximum_balance', {
                            point: maxPointCanUsed,
                            count: maxPointCanUsed,
                        }),
                    )
                    .when('discount', {
                        is: (discount: number) => newTotal < 0,
                        then: yup
                            .number()
                            .max(
                                maxPointCanUsed,
                                t('finish.warning.cannot_more_than_total_due'),
                            ),
                    }),
            }),
        ),
        mode: 'onChange',
    })
    const { setValue } = form

    const onChangeRedeemPoint = useCallback(
        (v: number) => {
            const redeemPoint = floorPoint(v)
            setValue('pointToRedeem', redeemPoint)
            const discount =
                min([
                    calculateRedeemedPointToDiscount(new Decimal(redeemPoint)),
                    maxDiscount.toNumber(),
                ]) ?? 0
            setValue('discount', discount)
            calculateNewTotalDueAfterDisocunt(new Decimal(discount))
        },
        [
            calculateNewTotalDueAfterDisocunt,
            calculateRedeemedPointToDiscount,
            floorPoint,
            maxDiscount,
            setValue,
        ],
    )

    return {
        form,
        customerPointBalance,
        requiredPointToRedeem,
        newTotal,
        onChangeRedeemPoint,
        maxPointCanUsed,
    }
}
export default usePaymentRedeemPoint
